import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FolderKanban, Search, Calendar, User as UserIcon, Loader2, ShieldCheck, Code, Bug, UserPlus, X, CheckCircle2, Trash2 } from "lucide-react";
import { setProject } from "../redux/authSlice";
import { useStarterPlan } from "../contexts/OrganizationPlanContext";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all"); 

  // Modal State
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedProjectForAssign, setSelectedProjectForAssign] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('developer'); // developer | qa
  const [userSearchQuery, setUserSearchQuery] = useState(''); 
  const [processing, setProcessing] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.currentUser);
  const starterBypass = useStarterPlan();

  const fetchData = async () => {
    if (!user?.user_id || !user?.org_id) return;
    try {
      setLoading(true);
      const [projRes, teamRes] = await Promise.all([
        axios.get(`https://api.testoraglobal.in/api/project/user/${user.user_id}`),
        axios.get(`https://api.testoraglobal.in/api/user/org-team/${user.org_id}`)
      ]);
      
      if (projRes.data.success) setProjects(projRes.data.projects);
      if (teamRes.data.success) setTeamMembers(teamRes.data.users);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [user]);

  // --- FILTERING ---
  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.project_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === "all" || p.user_role === activeTab;
    return matchesSearch && matchesTab;
  });

  // Modal Filtering
  const sortedModalUsers = [...teamMembers].sort((a, b) => a.full_name.localeCompare(b.full_name));
  const unassignedModalUsers = sortedModalUsers.filter(m => {
    if (!selectedProjectForAssign) return false;
    
    // 1. Check if user is ALREADY A MANAGER (This hides the logged-in user and co-managers)
    const isManager = selectedProjectForAssign.managers?.some(mgr => mgr.user_id === m.user_id);
    if (isManager) return false;

    // 2. Don't show users who are already assigned to the SELECTED ROLE
    const currentList = selectedRole === 'developer' ? selectedProjectForAssign.developers : selectedProjectForAssign.qa;
    return !currentList?.some(u => u.user_id === m.user_id);
  });
  
  const searchedModalUsers = unassignedModalUsers.filter(m => 
    m.full_name?.toLowerCase().includes(userSearchQuery.toLowerCase()) || 
    m.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  // --- HANDLERS ---
  const handleProjectClick = (project) => {
    dispatch(setProject(project));
    navigate(`/dashboard/${project.project_id}`);
  };

  const openAssignModal = (e, project) => {
    e.stopPropagation(); 
    setSelectedProjectForAssign(project);
    setIsAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedUserId('');
    setUserSearchQuery('');
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return toast.error("Please select a user");
    
    setProcessing(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/project/assign", { 
        project_id: selectedProjectForAssign.project_id,
        user_id: selectedUserId,
        role: selectedRole,
        org_id: user.org_id
      });
      if (res.data.success) {
        toast.success(res.data.message);
        
        // Optimistic UI Update
        const assignedUser = teamMembers.find(m => m.user_id === selectedUserId);
        setSelectedProjectForAssign(prev => {
          const listName = selectedRole === 'developer' ? 'developers' : 'qa';
          return { ...prev, [listName]: [...(prev[listName] || []), assignedUser] };
        });
        
        setSelectedUserId('');
        fetchData(); 
      } else toast.error(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || "Error assigning user"); } 
    finally { setProcessing(false); }
  };

  const handleRemoveUser = async (userId, role) => {
    setProcessing(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/project/unassign", { 
        project_id: selectedProjectForAssign.project_id,
        user_id: userId,
        role: role
      });
      if (res.data.success) {
        toast.success("User removed");
        
        // Optimistic UI Update
        setSelectedProjectForAssign(prev => {
          const listName = role === 'developer' ? 'developers' : 'qa';
          return { ...prev, [listName]: prev[listName].filter(m => m.user_id !== userId) };
        });
        
        fetchData();
      } else toast.error(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || "Error removing user"); } 
    finally { setProcessing(false); }
  };

  const renderRoleBadge = (role) => {
    switch (role) {
      case 'manager': return <span className="bg-purple-500/10 text-purple-400 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"><ShieldCheck size={12}/> Manager</span>;
      case 'developer': return <span className="bg-blue-500/10 text-blue-400 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"><Code size={12}/> Developer</span>;
      case 'qa': return <span className="bg-yellow-500/10 text-yellow-400 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"><Bug size={12}/> QA Tester</span>;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white p-10">
      <div className="max-w-7xl mx-auto">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-6">
          <div>
            <span className="px-3 py-1 rounded-full bg-purple-500/10 text-purple-500 text-[10px] font-bold uppercase tracking-widest mb-3 inline-block">
              My Workspace
            </span>
            <h1 className="text-4xl font-bold tracking-tight mb-2">My Assigned Projects</h1>
            <p className="text-gray-400 text-sm">
              Welcome back, <span className="text-white font-semibold">{user?.user_name}</span>
            </p>
          </div>
          
          <div className="relative w-full md:w-72">
            {/* <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} /> */}
            <input
              type="text"
              placeholder="Search my projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#0B1220] border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:border-purple-500 text-sm shadow-sm"
            />
          </div>
        </div>

        {/* ROLE TABS */}
        <div className="flex gap-6 border-b border-gray-800 mb-8 overflow-x-auto custom-scrollbar">
          <button onClick={() => setActiveTab('all')} className={`pb-3 font-medium flex items-center gap-2 transition-colors whitespace-nowrap ${activeTab === 'all' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}>All Assigned</button>
          <button onClick={() => setActiveTab('manager')} className={`pb-3 font-medium flex items-center gap-2 transition-colors whitespace-nowrap ${activeTab === 'manager' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}><ShieldCheck size={18}/> Managed by me</button>
          <button onClick={() => setActiveTab('developer')} className={`pb-3 font-medium flex items-center gap-2 transition-colors whitespace-nowrap ${activeTab === 'developer' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}><Code size={18}/> Developer</button>
          <button onClick={() => setActiveTab('qa')} className={`pb-3 font-medium flex items-center gap-2 transition-colors whitespace-nowrap ${activeTab === 'qa' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}><Bug size={18}/> QA Tester</button>
        </div>

        {/* CONTENT AREA */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-purple-500">
            <Loader2 size={40} className="animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-[#0B1220] border border-gray-800 rounded-3xl p-16 text-center shadow-lg">
            <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <FolderKanban size={32} className="text-gray-500" />
            </div>
            <h3 className="text-2xl font-semibold mb-2">No projects assigned</h3>
            <p className="text-gray-400 max-w-md mx-auto">You haven't been assigned to any projects yet. Please contact your organization administrator.</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="bg-[#0B1220]/50 border border-gray-800/50 rounded-3xl p-16 text-center">
             <Search size={32} className="text-gray-600 mx-auto mb-4" />
             <h3 className="text-xl font-medium text-gray-300 mb-2">No matches found</h3>
             <p className="text-gray-500 text-sm">No projects found for the selected role or search query.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {filteredProjects.map((project) => (
                <motion.div 
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  key={project.project_id} 
                  onClick={() => handleProjectClick(project)}
                  className="bg-[#0B1220] border border-gray-800 hover:border-purple-500/50 rounded-2xl p-6 transition-all cursor-pointer group shadow-lg flex flex-col relative"
                >
                  <div className="absolute top-4 right-4 flex items-center gap-2">
                     {renderRoleBadge(project.user_role)}
                     
                     {/* IF USER IS MANAGER, SHOW ASSIGN BUTTON */}
                     {(starterBypass || project.user_role === 'manager') && (
                       <button 
                         onClick={(e) => openAssignModal(e, project)} 
                         className="p-1.5 bg-gray-800/60 hover:bg-purple-600 border border-gray-700 hover:border-purple-500 rounded-lg text-gray-300 hover:text-white transition shadow-sm z-10"
                         title="Manage Team"
                       >
                         <UserPlus size={16} />
                       </button>
                     )}
                  </div>

                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-500 mb-4 group-hover:bg-purple-500 group-hover:text-white transition-colors shrink-0">
                    <FolderKanban size={24} />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-2 pr-28 truncate group-hover:text-purple-400 transition-colors">{project.project_name}</h3>
                  <p className="text-gray-400 text-sm mb-6 line-clamp-2 flex-1">
                    {project.project_description || "No description provided."}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-800/50 pt-4 mt-auto">
                    <div className="flex items-center gap-1.5 truncate pr-2">
                      <UserIcon size={14} className="shrink-0" />
                      <span className="truncate">
                        {project.managers && project.managers.length > 0 ? `Managed by ${project.managers.map(m=>m.full_name).join(", ")}` : "No Manager"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <Calendar size={14} className="shrink-0" />
                      {new Date(project.created_date).toLocaleDateString()}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ASSIGN TEAM MODAL FOR MANAGERS */}
      <AnimatePresence>
        {isAssignModalOpen && (
           <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
           <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !processing && closeAssignModal()} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
           <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} className="relative bg-[#0B1220] border border-gray-700 shadow-2xl rounded-2xl w-full max-w-4xl overflow-hidden flex flex-col h-[650px] max-h-[90vh]">
             
             {/* Header */}
             <div className="flex justify-between items-center p-6 border-b border-gray-800 shrink-0">
                <h2 className="text-xl font-bold">Manage Project Team</h2>
                <X size={24} className="cursor-pointer text-gray-400 hover:text-white transition" onClick={closeAssignModal}/>
             </div>

             <div className="p-6 flex-1 flex flex-col overflow-hidden">
               <p className="text-sm text-gray-400 mb-4 shrink-0">Build your team for <strong className="text-white">{selectedProjectForAssign?.project_name}</strong>.</p>
               
               {/* Two Column Grid */}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-8 flex-1 overflow-hidden min-h-0">
                 
                 {/* LEFT COLUMN: Currently Assigned */}
                 <div className="flex flex-col bg-[#111827] rounded-xl border border-gray-800 p-4 overflow-hidden">
                    <h4 className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-4 shrink-0">Current Team</h4>
                    
                    <div className="overflow-y-auto custom-scrollbar flex-1 pr-2 space-y-6">
                      
                      {/* Managers Section (Read Only) */}
                      <div>
                        <div className="flex items-center gap-2 mb-2 text-purple-400 font-semibold text-sm border-b border-gray-800 pb-1"><ShieldCheck size={16}/> Managers ({selectedProjectForAssign?.managers?.length || 0})</div>
                        <div className="space-y-2">
                          {selectedProjectForAssign?.managers?.length > 0 ? (
                            selectedProjectForAssign.managers.map(mgr => (
                              <div key={mgr.user_id} className="flex justify-between items-center bg-[#0B1220] p-2.5 rounded-lg border border-gray-800">
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <div className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">{mgr.full_name.charAt(0)}</div>
                                  <span className="text-sm font-medium text-white truncate">{mgr.full_name}</span>
                                </div>
                                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500 bg-gray-800/50 px-2 py-1 rounded">Manager</span>
                              </div>
                            ))
                          ) : <p className="text-xs text-gray-600 pl-2">No managers assigned.</p>}
                        </div>
                      </div>

                      {/* Developer Section */}
                      <div>
                        <div className="flex items-center gap-2 mb-2 text-blue-400 font-semibold text-sm border-b border-gray-800 pb-1"><Code size={16}/> Developers ({selectedProjectForAssign?.developers?.length || 0})</div>
                        <div className="space-y-2">
                          {selectedProjectForAssign?.developers?.length > 0 ? (
                            selectedProjectForAssign.developers.map(dev => (
                              <div key={dev.user_id} className="flex justify-between items-center bg-[#0B1220] p-2.5 rounded-lg border border-gray-800 hover:border-gray-700">
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold shrink-0">{dev.full_name.charAt(0)}</div>
                                  <span className="text-sm font-medium text-white truncate">{dev.full_name}</span>
                                </div>
                                <button onClick={() => handleRemoveUser(dev.user_id, 'developer')} disabled={processing} className="text-gray-500 hover:text-red-500 p-1.5 rounded transition disabled:opacity-50 shrink-0"><Trash2 size={16} /></button>
                              </div>
                            ))
                          ) : <p className="text-xs text-gray-600 pl-2">No developers assigned.</p>}
                        </div>
                      </div>

                      {/* QA Section */}
                      <div>
                        <div className="flex items-center gap-2 mb-2 text-yellow-400 font-semibold text-sm border-b border-gray-800 pb-1"><Bug size={16}/> QA Testers ({selectedProjectForAssign?.qa?.length || 0})</div>
                        <div className="space-y-2">
                          {selectedProjectForAssign?.qa?.length > 0 ? (
                            selectedProjectForAssign.qa.map(qa => (
                              <div key={qa.user_id} className="flex justify-between items-center bg-[#0B1220] p-2.5 rounded-lg border border-gray-800 hover:border-gray-700">
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <div className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center text-xs font-bold shrink-0">{qa.full_name.charAt(0)}</div>
                                  <span className="text-sm font-medium text-white truncate">{qa.full_name}</span>
                                </div>
                                <button onClick={() => handleRemoveUser(qa.user_id, 'qa')} disabled={processing} className="text-gray-500 hover:text-red-500 p-1.5 rounded transition disabled:opacity-50 shrink-0"><Trash2 size={16} /></button>
                              </div>
                            ))
                          ) : <p className="text-xs text-gray-600 pl-2">No QA assigned.</p>}
                        </div>
                      </div>

                    </div>
                 </div>

                 {/* RIGHT COLUMN: Assign New */}
                 <div className="flex flex-col overflow-hidden">
                    <h4 className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-4 shrink-0">Assign New User</h4>
                    
                    {/* Role Selector Tabs */}
                    <div className="flex gap-2 mb-4 shrink-0 bg-[#111827] p-1 rounded-lg border border-gray-800">
                      <button onClick={() => {setSelectedRole('developer'); setSelectedUserId('');}} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition ${selectedRole === 'developer' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>Developer</button>
                      <button onClick={() => {setSelectedRole('qa'); setSelectedUserId('');}} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition ${selectedRole === 'qa' ? 'bg-yellow-600 text-white' : 'text-gray-400 hover:text-white'}`}>QA Tester</button>
                    </div>

                    {/* Search Bar */}
                    <div className="relative mb-4 shrink-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                      <input type="text" placeholder={`Search users to assign as ${selectedRole}...`} value={userSearchQuery} onChange={(e) => setUserSearchQuery(e.target.value)} className="w-full bg-[#111827] border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:border-purple-500 text-sm transition-colors"/>
                    </div>

                    <form onSubmit={handleAssignUser} className="flex flex-col flex-1 overflow-hidden min-h-0">
                      {/* Scrollable list */}
                      <div className="overflow-y-auto space-y-2 mb-4 pr-2 custom-scrollbar flex-1">
                         {searchedModalUsers.length === 0 ? (
                            <p className="text-center text-gray-500 text-sm py-4 bg-[#111827] rounded-lg border border-gray-800">No available users found.</p>
                         ) : (
                           searchedModalUsers.map(member => (
                              <div 
                                key={member.user_id} 
                                onClick={() => setSelectedUserId(prev => prev === member.user_id ? '' : member.user_id)}
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition border ${selectedUserId === member.user_id ? 'bg-purple-600/20 border-purple-500' : 'bg-[#111827] border-gray-800 hover:border-gray-600'}`}
                              >
                                <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold text-xs shrink-0">{member.full_name.charAt(0)}</div>
                                <div className="overflow-hidden flex-1">
                                   <p className="text-sm font-semibold truncate text-white">{member.full_name}</p>
                                   <p className="text-xs text-gray-500 truncate">{member.email}</p>
                                </div>
                                {selectedUserId === member.user_id && <CheckCircle2 size={18} className="text-purple-500 shrink-0" />}
                              </div>
                           ))
                         )}
                      </div>

                      <div className="pt-4 border-t border-gray-800 shrink-0">
                         <button type="submit" disabled={processing || !selectedUserId} className="w-full bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2">
                           {processing ? <Loader2 className="animate-spin" size={18}/> : `Assign Selected ${selectedRole === 'developer' ? 'Developer' : 'QA'}`}
                         </button>
                      </div>
                    </form>
                 </div>
               </div>
             </div>
           </motion.div>
         </div>
        )}
      </AnimatePresence>

    </div>
  );
}